import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

interface UserProfile {
  id: string;
  email?: string;
  full_name?: string;
  avatar_url?: string;
  role?: string;
  group_roles?: Array<'admin' | 'clerk' | 'member'>;
}

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  /** True "UI role": platform admin OR group_members admin/clerk */
  effectiveRole: 'admin' | 'member';
  /** Groups where the user is a group admin/clerk via group_members.role (scoped admin) */
  adminGroupIds: string[];
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
  setError?: (error: string) => void;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, setError }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [adminGroupIds, setAdminGroupIds] = useState<string[]>([]);
  const [effectiveRole, setEffectiveRole] = useState<'admin' | 'member'>('member');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session - Use Promise chain
    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        if (session?.user) {
          setLoading(true);
          setUser(session.user);
          // IMPORTANT: keep `loading=true` until the profile (and derived roles) are loaded.
          // Otherwise pages can redirect based on incomplete userProfile/effectiveRole during refresh.
          fetchUserProfile(session.user.id)
            .finally(() => setLoading(false));
        } else {
          setLoading(false);
        }
      })
      .catch((error) => {
        console.error('Error getting session:', error);
        setLoading(false);
      });

    // Listen for auth changes - NEVER ASYNC callback
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event: string, session: Session | null) => {
        if (session?.user) {
          setLoading(true);
          setUser(session.user);
          fetchUserProfile(session.user.id)
            .finally(() => setLoading(false));
        } else {
          setUser(null);
          setUserProfile(null);
          setAdminGroupIds([]);
          setEffectiveRole('member');
          setLoading(false);
        }
      }
    );

    return () => subscription?.unsubscribe();
  }, []);

  const fetchUserProfile = async (userId: string): Promise<void> => {
    try {
      // Add connection check before query
      const { data: connectionTest, error: connectionError } = await supabase
        .from('user_profiles')
        .select('count')
        .limit(1)
        .maybeSingle();

      if (connectionError) {
        throw new Error(`Supabase connection failed: ${connectionError.message}`);
      }

      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (error) {
        // If user profile doesn't exist, create a basic one from auth user data
        if (error.code === 'PGRST116') {
          console.log('User profile not found, user may need to complete registration');
          setUserProfile(null);
          return;
        }
        throw error;
      }
      
      // Also load group-level roles (admin/clerk/member) from group_members.
      // This is used to grant admin UI access to assigned group admins without changing platform role.
      let group_roles: Array<'admin' | 'clerk' | 'member'> = [];
      try {
        const { data: memberRoles, error: memberRolesError } = await supabase
          .from('group_members')
          .select('role')
          .eq('user_id', userId);

        if (!memberRolesError && memberRoles) {
          const set = new Set<'admin' | 'clerk' | 'member'>();
          for (const row of memberRoles as any[]) {
            const r = row.role as 'admin' | 'clerk' | 'member' | undefined;
            if (r) set.add(r);
          }
          // Order roles for display: admin > clerk > member
          const ordered: Array<'admin' | 'clerk' | 'member'> = [];
          if (set.has('admin')) ordered.push('admin');
          if (set.has('clerk')) ordered.push('clerk');
          if (set.has('member')) ordered.push('member');
          group_roles = ordered;
        }
      } catch (e) {
        console.warn('Failed to load group member roles:', e);
      }

      setUserProfile({ ...(data as any), group_roles });

      // Load group-scoped admin access (group_members.role = admin/clerk)
      let groupScopedAdminIds: string[] = [];
      try {
        const { data: groupRoles, error: rolesError } = await supabase
          .from('group_members')
          .select('group_id, role')
          .eq('user_id', userId)
          .in('role', ['admin', 'clerk']);

        if (rolesError) {
          console.warn('Failed to load group member admin roles:', rolesError);
          groupScopedAdminIds = [];
        } else {
          groupScopedAdminIds = (groupRoles || [])
            .map((r: any) => r.group_id)
            .filter((g: any) => typeof g === 'string' && g.length > 0);
        }
      } catch (e) {
        console.warn('Failed to load group member admin roles:', e);
        groupScopedAdminIds = [];
      }
      setAdminGroupIds(groupScopedAdminIds);

      // Compute effective role for UI/nav:
      // platform group_creator/group_admin/super_admin OR group_members admin/clerk => admin UI
      const platformRole = (data?.role || '').toString();
      if (platformRole === 'super_admin' || platformRole === 'group_admin' || platformRole === 'group_creator' || platformRole === 'group_clerk') {
        setEffectiveRole('admin');
      } else {
        setEffectiveRole(groupScopedAdminIds.length > 0 ? 'admin' : 'member');
      }

      // Refresh subscription cache for offline use
      try {
        const { subscriptionService } = await import('../services/subscriptionService');
        await subscriptionService.refreshSubscriptionCache(userId);
      } catch (subError) {
        console.error('Error refreshing subscription cache:', subError);
        // Don't throw - subscription cache refresh failure shouldn't block login
      }
    } catch (error: any) {
      console.error('Error fetching user profile:', {
        message: error?.message || 'Unknown error',
        details: error?.stack || error?.toString(),
        hint: error?.hint || '',
        code: error?.code || ''
      });
      
      // Show user-friendly error message
      if (setError) {
        if (error?.message?.includes('connection') || error?.message?.includes('fetch')) {
          setError('Unable to connect to the server. Please check your internet connection and try again.');
        } else {
          setError('Failed to load user profile. Please try refreshing the page.');
        }
      }
    }
  };

  const signOut = async (): Promise<void> => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    userProfile,
    effectiveRole,
    adminGroupIds,
    loading,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};